import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { hasModule } from '../manage/auth'
import { buildManageNavSections } from '../manage/modules'
import '../manage/Manage.css'

function isItemActive(item, pathname) {
  if (item.exact) return pathname === item.path
  return pathname === item.path || pathname.startsWith(item.path + '/')
}

function sectionContainsPath(sectionItems, pathname) {
  return sectionItems.some((item) => isItemActive(item, pathname))
}

/** Sticky Manage nav bar — shown site-wide for users with Manage access. */
function ManageTopNav() {
  const location = useLocation()
  const navRef = useRef(null)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  const displaySections = useMemo(() => buildManageNavSections(hasModule), [])

  useEffect(() => {
    setOpenDropdown(null)
    setMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const onDocClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  const toggleDropdown = useCallback((sectionId) => {
    setOpenDropdown((prev) => (prev === sectionId ? null : sectionId))
  }, [])

  if (!displaySections.length) return null

  return (
    <div className="manage-topnav-sticky" ref={navRef}>
      <div className="container-fluid manage-topnav-inner">
        <Link to="/manage" className="manage-topnav-brand">
          Manage
        </Link>
        <button
          type="button"
          className="manage-topnav-toggler"
          aria-expanded={mobileOpen}
          aria-controls="manage-topnav-menu"
          onClick={() => setMobileOpen((v) => !v)}
        >
          <span className="manage-topnav-toggler-bar" />
          <span className="manage-topnav-toggler-bar" />
          <span className="manage-topnav-toggler-bar" />
          <span className="sr-only">Toggle manage menu</span>
        </button>
        <nav
          id="manage-topnav-menu"
          className={`manage-topnav-nav${mobileOpen ? ' is-open' : ''}`}
          aria-label="Manage navigation"
        >
          {displaySections.map((section) => {
            const isOpen = openDropdown === section.id
            const sectionActive = sectionContainsPath(
              section.items,
              location.pathname
            )
            return (
              <div
                key={section.id}
                className={`manage-topnav-section${sectionActive ? ' has-active' : ''}${isOpen ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="manage-topnav-section-toggle"
                  onClick={() => toggleDropdown(section.id)}
                  aria-expanded={isOpen}
                  aria-haspopup="true"
                  aria-controls={`manage-topnav-${section.id}`}
                  id={`manage-topnav-toggle-${section.id}`}
                >
                  <span>{section.label}</span>
                  <span className="manage-nav-chevron" aria-hidden="true">
                    {isOpen ? '▴' : '▾'}
                  </span>
                </button>
                <div
                  id={`manage-topnav-${section.id}`}
                  className={`manage-topnav-dropdown${isOpen ? ' is-open' : ''}`}
                  role="menu"
                  aria-labelledby={`manage-topnav-toggle-${section.id}`}
                  hidden={!isOpen}
                >
                  {section.items.map((item) => {
                    const active = isItemActive(item, location.pathname)
                    return (
                      <Link
                        key={item.key}
                        to={item.path}
                        role="menuitem"
                        className={`manage-topnav-link${active ? ' active' : ''}`}
                        onClick={() => {
                          setOpenDropdown(null)
                          setMobileOpen(false)
                        }}
                      >
                        {item.label}
                      </Link>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </nav>
      </div>
    </div>
  )
}

export default ManageTopNav
